import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import LanguageSelector from '../components/LanguageSelector';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';
import useCanonical from '../hooks/useCanonical';

// Default brand colors
const DEFAULT_BLUE = '#2B4B6A';
const DEFAULT_ORANGE = '#F58A5A';

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [noPasswordSet, setNoPasswordSet] = useState(false);
  const [platformTheme, setPlatformTheme] = useState(null);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { company, isSubdomain, loading: subdomainLoading } = useSubdomain();

  // Fetch platform theme and OAuth status
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Always fetch OAuth status
        const oauthRes = await api.get('/integrations/google-oauth/status');
        setGoogleOAuthEnabled(oauthRes.data.enabled);
        
        // Only fetch platform theme for main domain
        if (!isSubdomain) {
          const themeRes = await api.get('/settings/public-theme');
          setPlatformTheme(themeRes.data);
        }
      } catch (error) {
        console.log('Using default theme');
      }
    };
    fetchSettings();
  }, [isSubdomain]);

  // Get company branding colors (for subdomain) or platform theme (for main domain)
  const primaryColor = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.primary_color || DEFAULT_BLUE);
  const accentColor = isSubdomain 
    ? (company?.theme_settings?.accent_color || DEFAULT_ORANGE)
    : (platformTheme?.secondary_color || DEFAULT_ORANGE);
  const buttonBg = isSubdomain 
    ? (company?.theme_settings?.primary_color || DEFAULT_BLUE)
    : (platformTheme?.button_primary_bg || DEFAULT_BLUE);
  const buttonText = isSubdomain 
    ? '#FFFFFF'
    : (platformTheme?.button_primary_text || '#FFFFFF');
  const pageBgColor = isSubdomain 
    ? (company?.theme_settings?.external_page_bg || '#F9FAFB')
    : (platformTheme?.page_bg || '#F9FAFB');
  
  const companyName = company?.name || 'Trados';

  // Set page title based on subdomain
  usePageTitle(isSubdomain ? `Login - ${companyName}` : 'Login');
  // Canonical only applies to the main domain — subdomain tenants are a different host
  useCanonical(isSubdomain ? null : '/login');

  // Handle Google OAuth login
  const handleGoogleLogin = async () => {
    try {
      // Get the OAuth URL from our backend (uses custom credentials if configured)
      // Pass the current origin so we can redirect back after OAuth
      const currentOrigin = window.location.origin;
      const response = await api.get('/auth/google/url', {
        params: { 
          redirect_uri: window.location.origin + '/auth/google/callback',
          origin: currentOrigin  // Pass origin for subdomain redirect
        }
      });
      
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error('Google OAuth is not properly configured');
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      toast.error('Failed to initialize Google login');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // If on subdomain, include company_id in login
      const loginData = { email, password };
      if (isSubdomain && company?.id) {
        loginData.company_id = company.id;
      }
      
      const user = await login(email, password, company?.id);
      toast.success(`Welcome back${isSubdomain ? ` to ${companyName}` : ''}!`);
      
      // Redirect company admins to setup wizard if they haven't completed it.
      // App pages live under /admin (domain consolidation: WordPress owns the
      // root, the React app lives at www.trados.app/admin).
      if (user.role === 'company_admin' && !user.has_completed_setup && isSubdomain) {
        navigate('/admin/company/setup');
      } else {
        navigate(user.role === 'admin' || user.role === 'company_admin' ? '/admin' : '/admin/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'no_password_set') {
        setNoPasswordSet(true);
      } else {
        setNoPasswordSet(false);
        toast.error(detail || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while detecting subdomain
  if (subdomainLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: pageBgColor }}>
        <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show loading state while fetching subdomain data to prevent theme flash
  if (isSubdomain && subdomainLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin" 
             style={{ borderTopColor: '#2B4B6A', borderWidth: '3px' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: pageBgColor }}>
      {/* Main domain: WP marketing chrome. Subdomain: company-branded header.
          PublicHeader owns that branch — do NOT render WPChrome directly here,
          or tenant subdomains get the Trados marketing header. */}
      <PublicHeader />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
          >
            {/* Language Selector - positioned above the header */}
            <div className="flex justify-end mb-4">
              <LanguageSelector variant="default" />
            </div>

            {/* Header — no in-card logo: the branded PublicHeader already shows
                the company logo, and light logos are invisible on the white card */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Welcome Back</h1>
              <p className="text-gray-500 text-sm">
                {isSubdomain 
                  ? `Sign in to your ${companyName} account`
                  : 'Sign in to access your dashboard'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': primaryColor }}
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm font-medium transition hover:underline"
                    style={{ color: primaryColor }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {noPasswordSet && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                  <p className="text-amber-800 font-medium mb-1">No password set for this account</p>
                  <p className="text-amber-700">
                    This account was created with Google Sign-In. You can{' '}
                    <Link to="/forgot-password" className="font-medium underline" style={{ color: primaryColor }}>
                      set a password here
                    </Link>{' '}
                    or use the <strong>Continue with Google</strong> button below.
                  </p>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-medium transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{ backgroundColor: buttonBg, color: buttonText }}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Google OAuth Login Button - Shown if enabled on main domain or subdomains */}
              {googleOAuthEnabled && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-white text-gray-500">or</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-button"
                    className="w-full flex items-center justify-center gap-3 py-3 px-6 rounded-xl font-medium border-2 border-gray-200 bg-white text-gray-700 transition-all hover:bg-gray-50 hover:border-gray-300"
                  >
                    <GoogleIcon />
                    Continue with Google
                  </button>
                </>
              )}
            </form>

            {/* Only show register link if NOT on subdomain (users register through main site) */}
            {!isSubdomain && (
              <div className="mt-6 text-center">
                <p className="text-gray-500 text-sm">
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    data-testid="login-register-link" 
                    className="font-medium transition"
                    style={{ color: accentColor }}
                  >
                    Create one now
                  </Link>
                </p>
              </div>
            )}

            {/* Subdomain info */}
            {isSubdomain && (
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-xs">
                  Powered by <a href="https://trados.app" className="hover:underline" style={{ color: primaryColor }}>Trados</a>
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default LoginPage;
